import { useState, useEffect, createContext, useContext, useCallback, useMemo, useRef } from 'react'

import axios from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import { useMessage } from './useMessage'
import { shouldPreserveKey } from '../utils/localStoragePreserve'
import { qk } from '../constants/queryKeys'


// 单一 401 跳转入口：cookie 鉴权下，401 即未登录或过期
let _on401 = null
export function setOn401Handler(fn) { _on401 = fn }

// R-fix-2026-08-21 P1 #4: 暴露 trigger 入口给 main.jsx 全局 axios 拦截器调用
// 优先于直接 redirect 的原因:让 React Query 缓存和 user state 先清空,避免用户
// 在 redirect 途中(浏览器地址栏已切 /login 但 React tree 还在跑)看到 stale 权限数据
export function trigger401Handler() {
  // R-fix-2026-08-24 (R5-D2-004): 与 main.jsx 拦截器一致 — login/logout 过渡态不 redirect,
  // 避免正在登录时 cookie 还没完全传到所有 in-flight 请求触发 401 → 误踢回 /login
  // (R-fix-2026-08-02 main.jsx 拦截器已加,这里触发入口漏了导致 trigger401Handler
  //  可以绕过 _loggingIn 屏蔽)
  if (typeof window !== 'undefined' && window._loggingIn) return
  try {
    if (_on401) _on401()
  } catch (_) { /* handler 失败不阻断 redirect */ }
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login?expired=1'
  }
}

// 退出登录标志:防止 logout 过程中后台轮询(NotificationsBell/org-summary 等)
// 用已清 cookie 触发 401 → /login?expired=1。R-fix-2026-08-01 恢复:之前某次清理
// 403 逻辑时一起删了,导致登出误跳。
let _loggingOut = false
export function isLoggingOut() { return _loggingOut }

// R-fix-2026-09-24: 在 window 上挂标志,给 queryCache.onError (main.jsx) 用于静默
// 过渡期 in-flight 请求的 401 噪音(client.js axios 拦截器已用 isLoggingOut() 阻止 redirect,
// 但 React Query onError 仍会 console.error)
function _setLoggingOut(v) {
  _loggingOut = v
  if (typeof window !== 'undefined') window.__pmworkLoggingTransition = v
}

// R-fix-2026-08-02: 登录过渡态标志 — login() 从拿到 cookie → queryClient.clear()
// → fetchUser() 中间窗口有"已飞出去但还没收到响应"的请求,这些请求没有新 cookie 会 401。
// 拦截器单看 isLoggingOut 会漏掉 login,导致跳 /login?expired=1(用户刚刚才登录)。
let _loggingIn = false
export function isLoggingIn() { return _loggingIn }
function _setLoggingIn(v) {
  _loggingIn = v
  if (typeof window !== 'undefined') window.__pmworkLoggingTransition = v
}

// R159-6: 403 全局友好提示 — 之前只有 401 跳登录,403 业务层看不到理由,
// 用户看到 0 数据以为是 bug。
// R-fix-2026-08-20: 改"任意 URL 5s 内只弹一次"。用户反馈"一个页面弹出多个
// '当前账号无该资源访问权限'",根因是 mount 时多个 query 同时 403(如 dianqi
// 之类没项目的账号进 /projects → OrgSummaryCard 调多个 endpoint 都 403)。
// 之前"同 URL 10s 内只一次"对不同 URL 无效 → 改窗口级去重。
// 文案也改:从"无该资源访问权限"(误导用户以为是账号问题)
// 改为"页面部分资源加载失败"(中性,可能是项目权限/账号权限/数据未配置等)
let _last403Toast = 0
let _last403WindowUrls = []  // 当前窗口内已发生 403 的 URL 列表,仅用于计数(不弹)

// 全局 axios 默认带 cookie（HttpOnly）
axios.defaults.withCredentials = true

axios.interceptors.response.use(
  (response) => {
    // R-fix-2026-08-22: 检测项目权限拒绝标记。
    // 后端 main.py:@app.exception_handler(StarletteHTTPException) 把项目级 403
    // (X-Permission-Code ∈ {no_pmp_record, pmp_no_view, pmp_no_edit, no_org_membership})
    // 转成 200 + _permission_denied 字段。这里 dispatch 事件让 ProjectPermissionBanner
    // 显示常驻提示,避免浏览器把 4xx 写到 console(用户硬要求:无 console 报错)。
    const denied = response.data?._permission_denied
    if (denied && denied.code && denied.module) {
      try {
        window.dispatchEvent(new CustomEvent('project-permission-denied', {
          detail: { code: denied.code, module: denied.module, url: response.config?.url || '' }
        }))
      } catch (_) { /* SSR / no window 静默 */ }
      // R-fix-2026-09-06: 归一为 items 数组 — 全站 30+ 处 `const {data:x=[]}` 消费
      // 该通道时拿到对象会 .map 整页崩(dianqi 成员 SafetyQuality/Schedule 实证)。
      // Banner 事件已在上方携带完整 detail, 此处归一不影响提示。
      if (Array.isArray(response.data?.items)) {
        response.data = response.data.items
      }
    }
    return response
  },
  (error) => {
    const status = error.response?.status
    const url = error.config?.url || ''
    // R-fix-2026-08-23: 移除 401 直接 _on401()。原因: 这里(useAuth.jsx 模块顶层)注册的
    // 全局 axios 响应拦截器先于 main.jsx 的 refresh 拦截器执行。原来 401 时这里立刻清 user +
    // 弹"登录已过期", 而 main.jsx 拦截器后面才 refresh 成功 retry —— 但 user 已被清空, App
    // 看到 user=null 重定向登录页, 造成"正常使用中被踢出"。401 的完整处理(refresh → 成功
    // retry / 失败再 trigger401Handler)统一交给 main.jsx 拦截器, 这里只保留 403 友好提示。
    if (status === 403) {
      // R-fix-2026-08-22: 项目权限拒绝已经走 200 + _permission_denied 通道,
      // 这里只处理\"非项目权限\"的 403(org_not_open / admin_not_granted 等)。
      const now = Date.now()
      const windowMs = 5000  // 5s 窗口
      // 窗口已过期 → 重置
      if (now - _last403Toast > windowMs) {
        _last403WindowUrls = []
        _last403Toast = now
      }
      _last403WindowUrls.push(url)
      // 窗口内第一个 403 才弹 toast
      if (_last403WindowUrls.length === 1) {
        try {
          // antd message 静态 API 能在 ConfigProvider 外调用,这里用动态 import 避免循环依赖
          import('antd').then(({ message: msgApi }) => {
            msgApi.warning('页面部分资源加载失败,请刷新或联系管理员', 4)
          })
        } catch (_) { /* antd 未就绪静默 */ }
      }
    }
    return Promise.reject(error)
  }
)

const AuthContext = createContext(null)
const ProjectsContext = createContext(null)
const ProjectsFetchedContext = createContext(false)

// ============================================================
// R-fix-2026-08-20 B.2: localStorage 最近访问记录
// ============================================================
// 目的: 新登录用户跳到权限内"上次访问的 module + project",
//       避免每次都跳默认首页 /schedule/0 让用户重新选。
//
// 设计决策:
// - 按 userId 隔离 localStorage key, 跨账号不污染
// - 数据: { module, projectId, ts }
//   module: 上次访问的模块名 (schedule/design/dashboard 等)
//   projectId: 上次访问的项目 id
//   ts: Date.now(), 预留多项目排序用
// - 写入: ProjectGuard 在有权访问时调 setLastVisited
// - 读取: ProjectsProvider useEffect 重排 (lastVisited 项目移到第一位)
// - 失败容忍: try/catch 包裹所有 localStorage 操作,
//            SSR/private mode/QuotaExceeded 都不会崩
//
// 不存的东西:
// - 不存 module 顺序 (前端菜单按 canViewModule 过滤后顺序固定, 不需要持久化)
// - 不存项目顺序 (ProjectsProvider 在 user 切换后整体重排)
// - 不存完整 projects 列表 (太大, 后端是 source of truth)

const LAST_VISITED_KEY_PREFIX = 'pmwork_last_visited_'

export function getLastVisited(userId) {
  if (!userId) return null
  try {
    const raw = localStorage.getItem(LAST_VISITED_KEY_PREFIX + userId)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (typeof parsed?.module === 'string' && typeof parsed?.projectId === 'number') {
      return parsed
    }
    return null
  } catch {
    return null
  }
}

export function setLastVisited(userId, module, projectId, selectedProjectId) {
  if (!userId || !module || !projectId) return
  try {
    // R-fix-2026-08-24 (R5-D4-002): App.jsx:237 传 4 参数,第 4 个 selectedProjectId 用于
    // dropdown 切换项目时持久化『用户最近手动选择的项目』,重登后 useProjectSelection
    // 兜底能从 lastVisited.selectedProjectId 恢复(否则永远走 projects[0].id)
    localStorage.setItem(LAST_VISITED_KEY_PREFIX + userId, JSON.stringify({
      module,
      projectId,
      selectedProjectId: selectedProjectId ?? projectId,
      ts: Date.now(),
    }))
  } catch {
    // QuotaExceeded / private mode / SSR 等, 不阻断主流程
  }
}

// 模块顺序 (与 App.jsx 菜单 L0-L3 顺序对齐, B.3 默认 landing 用)
// 用户没 localStorage 记录时, 按这个顺序找第一个 canViewModule=true 的模块
export const MODULE_ORDER = [
  'dashboard', 'schedule', 'documents', 'design', 'contract', 'material',
  'meeting', 'safety-quality', 'procedures', 'shared-data',
]

export function ProjectsProvider({ children, projects = [], isFetched = true }) {
  const user = useContext(AuthContext)
  // R-fix-2026-08-24 (R5-D4-004): 改 useMemo 替代 useState+useEffect,首帧即正确顺序
  // (之前模式在 useEffect 执行前 projects[0] 是后端 id 升序第一个,useProjectSelection 兜底取错,
  //  导致 dropdown 切项目 → 登出 → 重登后跳到错误项目)
  const ordered = useMemo(() => {
    if (!user || !isFetched || projects.length === 0) return projects
    const last = getLastVisited(user.id)
    if (last && projects.some(p => p.id === last.projectId)) {
      const target = projects.find(p => p.id === last.projectId)
      const rest = projects.filter(p => p.id !== last.projectId)
      return [target, ...rest]
    }
    return projects
  }, [user, isFetched, projects])

  return (
    <ProjectsContext.Provider value={ordered}>
      <ProjectsFetchedContext.Provider value={isFetched}>
        {children}
      </ProjectsFetchedContext.Provider>
    </ProjectsContext.Provider>
  )
}

export function useProjects() {
  return useContext(ProjectsContext) || []
}

export function useProjectsFetched() {
  return useContext(ProjectsFetchedContext)
}

export function AuthProvider({ children }) {
  const message = useMessage()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()
  const fetchUserRef = useRef(null)
  // 跟踪"是否曾登录过",用于区分"初始探测 401"(未登录过,不该弹"过期"提示)
  // 和"会话过期 401"(曾登录,需要提示)。user state 闭包不能拿最新值,必须用 ref
  const userRef = useRef(null)

  const fetchUser = useCallback(async () => {
    // R-audit-2026-08-26: 用 /diagnose 替代 /me,区分失败原因让前端能给精准提示:
    //   - token_expired → "登录已过期"
    //   - user_disabled → "账号已被禁用,请联系管理员"
    //   - org_disabled → "所在组织已被禁用"
    //   - pwd_ver_mismatch → "密码已修改"
    // 老 /me 把所有失败统一返 null,用户体验只能"跳 /login"
    try {
      const res = await axios.get('/api/auth/diagnose')
      const { user: u, reason, detail } = res.data || {}
      if (u) {
        setUser(u)
      } else {
        setUser(null)
        // reason 不是 'no_token'(意味着登录过)/ 'unknown_error'(系统问题)/ 'token_invalid' 时,
        // 给用户弹一个轻提示。已登录用户 + token_expired 由 401 拦截器单独处理,
        // 这里只处理「曾经登录过但状态变了」场景(被禁用/组织禁用/过期等)
        const wasLoggedIn = !!userRef.current
        if (wasLoggedIn && detail && !['no_token', 'unknown_error'].includes(reason)) {
          // 用 message 替代 modal(简单),detail 已经是人类可读
          try {
            message.warning(detail, 5)
          } catch {}
        }
      }
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  fetchUserRef.current = fetchUser

  useEffect(() => {
    userRef.current = user
  }, [user])

  useEffect(() => {
    // 注册全局 401 跳转：清掉 user + 提示
    setOn401Handler(() => {
      const wasLoggedIn = !!userRef.current
      setUser(null)
      queryClient.clear()
      // R2-P0-6: 401 过期同样清 localStorage(防止下个用户拿到草稿/草稿密码)
      try {
        const toRemove = []
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i)
          // R-fix-2026-09-06: Onboarding「下次不再显示」标记是 per-user 偏好,一并保留
          // (此前 login/logout/401 三处清理都删它 → 每次登录重新弹引导,用户反馈勾选无效)
          // R-2026-09-21: 抽 shouldPreserveKey — 保留 KPI/列宽/折叠/权重 等 per-user UI 偏好,
          // 否则用户退出登录就"重置",违反预期。
          if (k && !shouldPreserveKey(k)) toRemove.push(k)
        }
        toRemove.forEach(k => localStorage.removeItem(k))
        sessionStorage.clear()
      } catch {}
      if (wasLoggedIn && window.location.pathname !== '/') {
        message.error('登录已过期，请重新登录')
      }
    })
    fetchUserRef.current()
  }, [queryClient])

  const login = useCallback(async (username, password) => {
    // R-fix-2026-08-02: 进入登录过渡态,屏蔽 401 拦截器跳转(同 logout)
    _setLoggingIn(true)
    try {
      const formData = new FormData()
      formData.append('username', username)
      formData.append('password', password)
      const res = await axios.post('/api/auth/token', formData)
      // R-fix-2026-10-XX P1:不再写入 axios.defaults Bearer — 后端用 cookie 鉴权,
      // 全局 axios.defaults 会污染所有 instance;若某些场景必须用 Bearer,新建独立 axios instance
      // (cookie 自动随请求发出,无需前端再拼 header)
      // R-fix-2026-07-20: 登录时也彻底清缓存 + 非保留 localStorage 项。
      // 之前只 removeItem('currentProjectId') + invalidate qk.projects() — 老账号的
      // OrgSummaryCard / dashboard 等 queryKey 不含 user.id 的缓存会残留显示旧数据;
      // 用户切账号瞬间能看到"前任"的项目统计。新增 queryClient.clear() + 类似 logout 的
      // localStorage 清理。保留 theme / language / i18nextLng 三个全局偏好。
      try {
        const toRemove = []
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i)
          // R-fix-2026-09-06: Onboarding「下次不再显示」标记是 per-user 偏好,一并保留
          // (此前 login/logout/401 三处清理都删它 → 每次登录重新弹引导,用户反馈勾选无效)
          // R-2026-09-21: 抽 shouldPreserveKey — 保留 KPI/列宽/折叠/权重 等 per-user UI 偏好
          if (k && !shouldPreserveKey(k)) toRemove.push(k)
        }
        toRemove.forEach(k => localStorage.removeItem(k))
        sessionStorage.clear()
      } catch {}
      await queryClient.cancelQueries()
      // 必须 clear() 而不是仅 invalidate():org-summary / notifications-unread-count
      // 这些 key 不含 user.id,只 invalidate 不会清,跨账号瞬间会显示老账号数据。
      queryClient.clear()
      await fetchUser()
      queryClient.invalidateQueries({ queryKey: qk.projects() })
      message.success('登录成功')
    } finally {
      // 兜底窗口:500ms 让所有 in-flight 请求(无 cookie 的旧请求)收尾
      setTimeout(() => { _setLoggingIn(false) }, 500)
    }
  }, [queryClient, message, fetchUser])

  const logout = useCallback(async () => {
    // R-fix-2026-08-01: 在 await /api/auth/logout 之前先打 _loggingOut = true
    // 否则 logout 之前(或并发)已经在飞的请求(NotificationsBell/org-summary)
    // 用旧 cookie 拿到 200,logout 之后再发一次就会 401 → 拦截器误跳 /login?expired=1
    _setLoggingOut(true)
    try {
      await axios.post('/api/auth/logout')
    } catch {
      // 即便后端失败也继续清前端状态
    }
    // 顺序很关键:必须先 cancel/clear queryClient,再清 axios header,最后 setUser
    // 否则 setUser(null) 触发重渲染 → 各 useQuery 子组件(passive effect)重新发起请求
    // → header 已清/cookie 已删 → 一连串 401 报错(usePermissions/NotificationBell/org-summary/ProjectList)
    await queryClient.cancelQueries()
    queryClient.clear()
    // R-fix-2026-10-XX P1:不再 delete axios.defaults Authorization(已经不再写入)
    // 保留 delete 作为兼容兜底(若有旧版本残留);新代码不应再设
    delete axios.defaults.headers.common['Authorization']
    setUser(null)
    // R2-P0-6: 清空所有用户作用域 localStorage,防止跨账号残留
    // (pm_draft_* 草稿 / gantt-* / weights_* / currentProjectId / procedure_*/material_*/task_units/schedule-sort/ai-chat-session 等)
    // 保留 theme / language 等全局偏好 + per-user UI 状态
    try {
      const toRemove = []
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i)
        // R-fix-2026-09-06: Onboarding「下次不再显示」标记是 per-user 偏好,一并保留
        // R-2026-09-21: 抽 shouldPreserveKey — 保留 KPI/列宽/折叠/权重 等 per-user UI 偏好
        if (k && !shouldPreserveKey(k)) toRemove.push(k)
      }
      toRemove.forEach(k => localStorage.removeItem(k))
      // sessionStorage 同步清(里面也有项目级数据)
      sessionStorage.clear()
    } catch {}
    // 给 401 拦截器 200ms 时间窗口吃掉所有并发 401,再解锁,避免正常浏览场景被误屏蔽
    setTimeout(() => { _setLoggingOut(false) }, 200)
  }, [queryClient])

  // 修复:useMemo 包一层避免 Provider 每次都生成新对象,触发所有消费者重渲染
  const contextValue = useMemo(
    () => ({ user, loading, login, logout }),
    [user, loading, login, logout]
  )

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    return { user: null, loading: false }
  }
  return context
}
